import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useToast } from 'context/providers/ToastProvider';
import useToken from 'hooks/useToken';
import React, { useEffect, useState } from 'react';
import { Button, Card, Col, Form, InputGroup, Row, Spinner } from 'react-bootstrap';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { Link } from 'react-router-dom';
import ProductoCard from './ProductoCard';

const CatalogoProductos = () => {
    const { showErrorToast } = useToast();
    const { token, setToken } = useToken();
    const [productos, setProductos] = useState([]);
    const [cargando, setCargando] = useState(true);
    const [productosSeleccionados, setProductosSeleccionados] = useState([]);

    // Estados para paginación
    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize] = useState(20); // Número de elementos por página
    const [hasMoreData, setHasMoreData] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [totalRecords, setTotalRecords] = useState(0);

    // Estados para filtros
    const [filtroTipoProducto, setFiltroTipoProducto] = useState('');
    const [filtroEstado, setFiltroEstado] = useState('activo');
    const [filtroBusqueda, setFiltroBusqueda] = useState('');
    const [debouncedBusqueda, setDebouncedBusqueda] = useState('');

    // Efecto para el debounce de la búsqueda
    useEffect(() => {
        const timerId = setTimeout(() => {
            setDebouncedBusqueda(filtroBusqueda);
        }, 500); // Espera 500ms después de que el usuario deja de escribir
        return () => clearTimeout(timerId);
    }, [filtroBusqueda]);

    // Estados para estadísticas
    const [stats, setStats] = useState({
        total: 0,
        activos: 0,
        inactivos: 0,
        servicios: 0,
        sinStock: 0
    });

    // Tipos de producto únicos (se llenarán con la primera carga)
    const [tiposProducto, setTiposProducto] = useState([]);

    /**
     * Se ejecuta la primera vez y cuando cambian los filtros
     */
    useEffect(() => {
        setPageNumber(1);
        setHasMoreData(true);
        obtenerProductos(1, false);
    }, [filtroTipoProducto, filtroEstado, debouncedBusqueda]);

    /**
     * Function to get and show products with pagination
     */
    const obtenerProductos = async (page = 1, append = false) => {
        if (!append) {
            setCargando(true);
        } else {
            setLoadingMore(true);
        }

        // Preparar objeto con filtros y paginación
        const filtrosConPaginacion = {
            pageNumber: page,
            pageSize: pageSize,
            idCatTipoProducto: filtroTipoProducto ? parseInt(filtroTipoProducto) : null,
            vEstado: filtroEstado,
            vSearch: debouncedBusqueda
        };

        const response = await fetch("/api/Productos/ObtenerListaProductos",
            {
                method: 'POST',
                headers: {
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(filtrosConPaginacion)
            })
            .catch((error) => {
                showErrorToast('Hubo un problema con la peticion Fetch:' + error);
                console.log('Hubo un problema con la peticion Fetch:' + error);
            });

        if (response.ok) {
            console.log("==== OBTENER PRODUCTOS  ====");
            const data = await response.json();
            console.log("data: ", data);

            if (data.iResponseType == 1) {
                var ObjDetalles = JSON.parse(data.detalles);
                const nuevosProductos = ObjDetalles.table2 || [];
                const todosPrecios = ObjDetalles.table6 || [];

                // Construir mapa idProducto -> precios y enriquecer cada producto
                const preciosMap = {};
                todosPrecios.forEach(p => {
                    if (!preciosMap[p.idProducto]) preciosMap[p.idProducto] = [];
                    preciosMap[p.idProducto].push(p);
                });
                const productosConPrecios = nuevosProductos.map(p => ({
                    ...p,
                    precios: preciosMap[p.idProducto] || []
                }));

                if (append) {
                    setProductos(prev => [...prev, ...productosConPrecios]);
                } else {
                    setProductos(productosConPrecios);
                    setPageNumber(1);

                    // Actualizar estadísticas (solo en la primera carga)
                    if (ObjDetalles.table3) {
                        const statsData = ObjDetalles.table3[0];
                        setStats({
                            total: statsData?.total || 0,
                            activos: statsData?.activos || 0,
                            inactivos: statsData?.inactivos || 0,
                            servicios: statsData?.servicios || 0,
                            sinStock: statsData?.sinStock || 0
                        });
                    }

                    // Actualizar tipos de producto (solo en la primera carga)
                    if (ObjDetalles.table4) {
                        setTiposProducto(ObjDetalles.table4 || []);
                    }
                }

                // Verificar si hay más datos disponibles
                const totalRecordsFromResponse = ObjDetalles.table5?.[0]?.totalRecords || 0;
                setTotalRecords(totalRecordsFromResponse);
                setHasMoreData((page * pageSize) < totalRecordsFromResponse);

                console.log("Productos:", nuevosProductos);
            }
            else {
                showErrorToast(data.vMessage);
                if (!append) {
                    setProductos([]);
                    setHasMoreData(false);
                }
            }
        }
        else {
            showErrorToast(response.status + ' ' + response.statusText);
            if (response.status == 401) {
                setToken(null);
                location.href = "";
            }
            if (!append) {
                setProductos([]);
                setHasMoreData(false);
            }
        }

        if (!append) {
            setCargando(false);
        } else {
            setLoadingMore(false);
        }
    };

    /**
     * Función para cargar más datos (infinite scroll)
     */
    const cargarMasDatos = () => {
        if (hasMoreData && !loadingMore && !cargando) {
            const nextPage = pageNumber + 1;
            setPageNumber(nextPage);
            obtenerProductos(nextPage, true);
        }
    };

    /**
     * Componente para el trigger de infinite scroll
     */
    const InfiniteScrollTrigger = () => {
        const triggerRef = React.useRef();

        React.useEffect(() => {
            const observer = new IntersectionObserver(
                (entries) => {
                    if (entries[0].isIntersecting && hasMoreData && !loadingMore && !cargando) {
                        cargarMasDatos();
                    }
                },
                { threshold: 0.1 }
            );

            if (triggerRef.current) {
                observer.observe(triggerRef.current);
            }

            return () => {
                if (triggerRef.current) {
                    observer.unobserve(triggerRef.current);
                }
            };
        }, [hasMoreData, loadingMore, cargando, pageNumber]);

        if (!hasMoreData) return null;

        return (
            <div ref={triggerRef} className="text-center py-4">
                {loadingMore ? (
                    <div>
                        <Spinner animation="border" variant="primary" className="mb-2" />
                        <div className="text-muted small">Cargando más productos...</div>
                    </div>
                ) : (
                    <div className="text-muted small">Desplázate para cargar más</div>
                )}
            </div>
        );
    };

    /**
     * Resetear filtros y recargar
     */
    const handleResetFiltros = () => {
        setFiltroTipoProducto('');
        setFiltroEstado('activo');
        setFiltroBusqueda('');
        // Al actualizar los estados, el useEffect se encargará de realizar la búsqueda
    };



    /**
     * Alternar selección de producto
     */
    const toggleSeleccionProducto = (idProducto) => {
        setProductosSeleccionados(prev => {
            if (prev.includes(idProducto)) {
                return prev.filter(id => id !== idProducto);
            } else {
                return [...prev, idProducto];
            }
        });
    };

    return (
        <>

            <Card className="mb-3">
                <Card.Header className="bg-light">
                    {/* Estadísticas */}
                    <Row className="g-3 mb-3">
                        <Col xs={6} md className="text-center">
                            <div className="mb-1">
                                <h4 className="text-primary mb-0">{stats.total}</h4>
                            </div>
                            <span className="fw-normal fs--2 text-600">Total Productos</span>
                        </Col>
                        <Col xs={6} md className="text-center">
                            <div className="mb-1">
                                <h4 className="text-success mb-0">{stats.activos}</h4>
                            </div>
                            <span className="fw-normal fs--2 text-600">Activos</span>
                        </Col>
                        <Col xs={6} md className="text-center">
                            <div className="mb-1">
                                <h4 className="text-danger mb-0">{stats.inactivos}</h4>
                            </div>
                            <span className="fw-normal fs--2 text-600">Inactivos</span>
                        </Col>
                        <Col xs={6} md className="text-center">
                            <div className="mb-1">
                                <h4 className="text-info mb-0">{stats.servicios}</h4>
                            </div>
                            <span className="fw-normal fs--2 text-600">Servicios</span>
                        </Col>
                        <Col xs={6} md className="text-center">
                            <div className="mb-1">
                                <h4 className="text-warning mb-0">{stats.sinStock}</h4>
                            </div>
                            <span className="fw-normal fs--2 text-600">Sin Stock</span>
                        </Col>
                    </Row>

                    <hr />

                    {/* Filtros */}
                    <Form>
                        <Row className="flex-between-center g-2">
                            <Col xs={12} sm={6} md={6} lg={3} className="d-flex align-items-center pe-0 mb-2">
                                <Form.Select
                                    size="sm"
                                    value={filtroTipoProducto}
                                    onChange={(e) => setFiltroTipoProducto(e.target.value)}
                                >
                                    <option value="">Todos los tipos</option>
                                    {tiposProducto.map((tipo) => (
                                        <option key={tipo.idCatTipoProducto} value={tipo.idCatTipoProducto}>
                                            {tipo.vDescripcion}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Col>

                            <Col xs={12} sm={6} md={6} lg={2} className="d-flex align-items-center pe-0 mb-2">
                                <Form.Select
                                    size="sm"
                                    value={filtroEstado}
                                    onChange={(e) => setFiltroEstado(e.target.value)}
                                >
                                    <option value="">Todos los estados</option>
                                    <option value="activo">Activos</option>
                                    <option value="inactivo">Inactivos</option>
                                </Form.Select>
                            </Col>

                            <Col xs={12} sm={6} md={6} lg={3} className="d-flex align-items-center pe-0 mb-2">
                                <InputGroup size="sm">
                                    <Form.Control
                                        type="text"
                                        placeholder="Buscar por nombre, código, ID..."
                                        value={filtroBusqueda}
                                        onChange={(e) => setFiltroBusqueda(e.target.value)}
                                    />
                                    <InputGroup.Text className="bg-white">
                                        <FontAwesomeIcon icon={icon({ name: 'search', style: 'solid' })} />
                                    </InputGroup.Text>
                                </InputGroup>
                            </Col>

                            <Col xs={12} sm={6} md={6} lg={2} className="d-flex align-items-center pe-0 mb-2">
                                {cargando ? (
                                    <Button variant="falcon-default" size="sm" disabled>
                                        <Spinner
                                            as="span"
                                            animation="grow"
                                            size="sm"
                                            role="status"
                                            aria-hidden="true"
                                        />
                                        &nbsp;Buscando...
                                    </Button>
                                ) : (
                                    <>                                    </>
                                )}
                            </Col>
                        </Row>
                    </Form>
                </Card.Header>

                <Card.Body className="p-0">
                    {cargando ? (
                        <div className="p-4">
                            {[...Array(5)].map((_, index) => (
                                <Card key={index} className="mb-3">
                                    <Card.Body>
                                        <Row>
                                            <Col xs={12} md={2}>
                                                <Skeleton height={30} width={80} className="mb-2" />
                                                <Skeleton height={25} width={100} className="mb-2" />
                                                <Skeleton height={25} width={80} />
                                            </Col>
                                            <Col xs={12} md={6}>
                                                <Skeleton height={20} className="mb-2" />
                                                <Skeleton height={15} width={200} />
                                                <Skeleton height={15} width={180} />
                                                <Skeleton height={15} width={150} />
                                            </Col>
                                            <Col xs={12} md={4} className="text-end">
                                                <Skeleton height={30} width={120} className="mb-2" />
                                                <Skeleton height={25} width={100} />
                                            </Col>
                                        </Row>
                                    </Card.Body>
                                </Card>
                            ))}
                        </div>
                    ) : productos.length > 0 ? (
                        <div className="p-3">
                            <div className="mb-3 d-flex justify-content-between align-items-center">
                                <small className="text-muted">
                                    Mostrando {productos.length} de {totalRecords > 0 ? totalRecords : productos.length} productos
                                </small>
                                {productosSeleccionados.length > 0 && (
                                    <small className="text-primary fw-bold">
                                        {productosSeleccionados.length} producto{productosSeleccionados.length !== 1 ? 's' : ''} seleccionado{productosSeleccionados.length !== 1 ? 's' : ''}
                                    </small>
                                )}
                            </div>
                            {productos.map((producto) => (
                                <ProductoCard
                                    key={producto.idProducto}
                                    producto={producto}
                                    isSelected={productosSeleccionados.includes(producto.idProducto)}
                                    onToggleSelection={toggleSeleccionProducto}
                                />
                            ))}
                            <InfiniteScrollTrigger />
                        </div>
                    ) : (
                        <div className="text-center py-5">
                            <div className="mb-3">
                                <img src="/img/producto96.png" alt="No hay productos" height={60} className="opacity-50" />
                            </div>
                            <h5 className="text-muted">No se encontraron productos</h5>
                            <p className="text-muted">
                                No hay productos registrados con los filtros seleccionados
                            </p>
                            {(filtroTipoProducto !== '' || filtroEstado !== 'activo' || filtroBusqueda !== '') ? (
                                <Button variant="falcon-default" size="sm" onClick={handleResetFiltros}>
                                    Limpiar filtros
                                </Button>
                            ) : (
                                <Link to="/productos/nuevo">
                                    <Button variant="primary">
                                        <FontAwesomeIcon icon={icon({ name: 'plus', style: "solid" })} className="me-1" />
                                        Registrar primer producto
                                    </Button>
                                </Link>
                            )}
                        </div>
                    )}
                </Card.Body>
            </Card>
        </>
    );
};

export default CatalogoProductos;
